import type { SpecUsageSummary } from '@parcel/types';
import { formatSpecBillingDate } from './spec-billing.js';
import { specTrialEnded } from './spec-usage-guard.js';

export type SpecAllowanceKind = 'trial' | 'monthly' | 'free';
export type SpecAllowanceState =
  | 'not_started'
  | 'active'
  | 'limit_reached'
  | 'ended'
  | 'unavailable';

/** How the free trial or monthly AI usage reads, shared by the chat popover and the Usage page. */
export interface SpecAllowanceView {
  kind: SpecAllowanceKind;
  label: 'Free AI trial' | 'Monthly AI usage' | 'Free AI usage';
  state: SpecAllowanceState;
  /** Whole percent, floored; 100 once the limit is reached or the trial ended, 0 when nothing was granted. */
  percentUsed: number;
  /** The formatted period end: when a trial ends or monthly usage resets. */
  date: string | null;
}

const LABELS = {
  trial: 'Free AI trial',
  monthly: 'Monthly AI usage',
  free: 'Free AI usage',
} as const;

/** `now` is the same wall clock the usage guard reads, so a trial past its end date ends on both. */
export function specAllowanceView(summary: SpecUsageSummary, now: number): SpecAllowanceView {
  const kind: SpecAllowanceKind =
    summary.entitlementKind === 'trial'
      ? 'trial'
      : summary.entitlementKind === 'free_monthly'
        ? 'free'
        : 'monthly';
  const base = { kind, label: LABELS[kind], date: formatSpecBillingDate(summary.periodEnd) };

  if (specTrialEnded(summary, now)) return { ...base, state: 'ended', percentUsed: 100 };
  if (summary.includedGranted <= 0) {
    // The trial grant is created by the first message, so zero granted means not started yet.
    return { ...base, state: kind === 'trial' ? 'not_started' : 'unavailable', percentUsed: 0 };
  }
  if (summary.includedRemaining <= 0) {
    return { ...base, state: 'limit_reached', percentUsed: 100 };
  }
  const percentUsed = Math.min(
    99,
    Math.max(0, Math.floor((summary.includedUsed / summary.includedGranted) * 100)),
  );
  return { ...base, state: 'active', percentUsed };
}

const OUT_OF = {
  trial: 'free trial usage',
  monthly: 'monthly AI usage',
  free: 'free AI usage',
} as const;

/** The caption under the meter: a date, what ran out, or what to expect before the first grant. */
export function specAllowanceNote(view: SpecAllowanceView): string | null {
  switch (view.state) {
    case 'not_started':
      return 'Starts with your first message';
    case 'unavailable':
      return null;
    case 'limit_reached':
      return `You have run out of ${OUT_OF[view.kind]}`;
    case 'ended':
      return view.date ? `Ended ${view.date}` : null;
    case 'active':
      if (!view.date) return null;
      return view.kind === 'trial' ? `Ends ${view.date}` : `Resets ${view.date}`;
  }
}

/** The badge beside the figure, when the state needs one. */
export function specAllowanceBadge(view: SpecAllowanceView): string | null {
  switch (view.state) {
    case 'limit_reached':
      return 'Limit reached';
    case 'ended':
      return 'Ended';
    case 'not_started':
      return 'Not started';
    case 'unavailable':
      return 'Not available';
    case 'active':
      return null;
  }
}

/** The one action beside the caption once usage runs out: what each plan can do about it. */
export function specAllowanceAction(view: SpecAllowanceView): string | null {
  if (view.state === 'ended') return 'Add AI seats';
  if (view.state !== 'limit_reached') return null;
  if (view.kind === 'trial') return 'Add AI seats';
  return view.kind === 'free' ? 'Upgrade' : 'Buy credits';
}
